import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

const CART_RESERVATION_STORAGE_KEY = 'arctavo-cart-reservation-ends-at';
const CART_RESERVATION_DURATION_MS = 10 * 60 * 1000;
const CART_RESERVATION_URGENT_MS = 3 * 60 * 1000;
let cartReservationEndsAt = 0;
let cartReservationItemCount = 0;
let cartReservationInterval = null;

function readCartReservationEnd() {
  try {
    const storedEnd = Number(window.localStorage.getItem(CART_RESERVATION_STORAGE_KEY));
    return Number.isFinite(storedEnd) && storedEnd > Date.now() ? storedEnd : 0;
  } catch {
    return 0;
  }
}

function storeCartReservationEnd(timestamp) {
  try {
    if (timestamp) window.localStorage.setItem(CART_RESERVATION_STORAGE_KEY, String(timestamp));
    else window.localStorage.removeItem(CART_RESERVATION_STORAGE_KEY);
  } catch {
    // Keep the timer usable for this page view when browser storage is unavailable.
  }
}

function renderCartReservationTimer() {
  const drawer = document.querySelector('#cart-drawer');
  const inner = drawer?.querySelector('.cart-drawer__inner');
  const header = inner?.querySelector(':scope > .theme-drawer__header');
  let timer = inner?.querySelector('[data-arctavo-cart-reservation]');
  const cartIsEmpty = !inner?.querySelector('.cart-form');

  if (!inner || !header || cartReservationItemCount < 1 || cartIsEmpty || inner.classList.contains('cart-drawer--empty')) {
    if (inner && (cartIsEmpty || inner.classList.contains('cart-drawer--empty'))) {
      cartReservationItemCount = 0;
      cartReservationEndsAt = 0;
      storeCartReservationEnd(0);
      if (cartReservationInterval !== null) {
        window.clearInterval(cartReservationInterval);
        cartReservationInterval = null;
      }
    }
    timer?.remove();
    return;
  }

  if (!timer) {
    timer = document.createElement('div');
    timer.className = 'arctavo-cart-reservation';
    timer.dataset.arctavoCartReservation = '';
    timer.setAttribute('role', 'timer');
    timer.setAttribute('aria-live', 'off');
    header.insertAdjacentElement('afterend', timer);
  }

  let timerLabel = timer.querySelector('[data-arctavo-cart-reservation-label]');
  let timerValue = timer.querySelector('[data-arctavo-cart-reservation-value]');
  if (!timerLabel || !timerValue) {
    timerLabel = document.createElement('span');
    timerLabel.dataset.arctavoCartReservationLabel = '';
    timerLabel.textContent = 'Cart reserved for';
    timerValue = document.createElement('span');
    timerValue.dataset.arctavoCartReservationValue = '';
    timer.replaceChildren(timerLabel, timerValue);
  }

  const now = Date.now();
  if (!cartReservationEndsAt || cartReservationEndsAt <= now) {
    cartReservationEndsAt = now + CART_RESERVATION_DURATION_MS;
    storeCartReservationEnd(cartReservationEndsAt);
  }

  const remainingMs = Math.max(0, cartReservationEndsAt - now);
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  timerValue.textContent = `${minutes}:${seconds}`;
  timer.setAttribute('aria-label', `Cart reserved for ${minutes}:${seconds}`);
  timer.classList.toggle('is-urgent', remainingMs > CART_RESERVATION_URGENT_MS);
  timer.hidden = false;
}

function setCartReservationItemCount(itemCount) {
  cartReservationItemCount = Math.max(0, Number(itemCount) || 0);

  if (cartReservationItemCount < 1) {
    cartReservationEndsAt = 0;
    storeCartReservationEnd(0);
    if (cartReservationInterval !== null) {
      window.clearInterval(cartReservationInterval);
      cartReservationInterval = null;
    }
    renderCartReservationTimer();
    return;
  }

  if (!cartReservationEndsAt) cartReservationEndsAt = readCartReservationEnd();
  if (!cartReservationEndsAt) {
    cartReservationEndsAt = Date.now() + CART_RESERVATION_DURATION_MS;
    storeCartReservationEnd(cartReservationEndsAt);
  }

  renderCartReservationTimer();
  if (cartReservationInterval === null) {
    cartReservationInterval = window.setInterval(renderCartReservationTimer, 1000);
  }
}

function getCartReservationItemCount(component) {
  const count = Number(component.querySelector('[ref="cartItemCount"]')?.textContent);
  return Number.isFinite(count) ? count : 0;
}

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  /** @type {MutationObserver | null} */
  #cartItemsObserver = null;
  /** @type {Element | null} */
  #observedCartItems = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    setCartReservationItemCount(getCartReservationItemCount(this));
    this.#observeCartItems();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.#cartItemsObserver?.disconnect();
    this.#cartItemsObserver = null;
    this.#observedCartItems = null;
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => {
      this.#observeCartItems();
      this.#updateStickyState();
      setCartReservationItemCount(getCartReservationItemCount(this));
    });
  };

  #observeCartItems() {
    const cartItems = this.querySelector('.cart-items-component');
    if (cartItems === this.#observedCartItems) return;

    this.#cartItemsObserver?.disconnect();
    this.#observedCartItems = cartItems;
    if (!cartItems) return;

    this.#cartItemsObserver = new MutationObserver(() => {
      const itemCount = getCartReservationItemCount(this);
      setCartReservationItemCount(itemCount);
    });
    this.#cartItemsObserver.observe(cartItems, { childList: true, subtree: true, characterData: true });
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#observeCartItems();
    this.#updateStickyState();
    setCartReservationItemCount(getCartReservationItemCount(this));

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.didError && Number.isFinite(Number(detail?.itemCount))) {
          setCartReservationItemCount(detail.itemCount);
          requestAnimationFrame(() => {
            this.#observeCartItems();
            setCartReservationItemCount(getCartReservationItemCount(this));
          });
        }

        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
